// libs
import React, { memo, useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// components
import PincodeInput from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeInput';

//styles
import { PincodeContainer, DeliveryText, PincodeInputContainer, TrailingButton } from './styles';

// helpers
import { checkPinCodeService } from 'shared/api/pin-code-service';
import { getPinCodeLabel, getPinCodeDataFromStorage, setPinCodeToStorage } from './helpers';

// hooks
import { useOutsideAlerter } from 'shared/layouts/DetailsPage/components/ProductInfo/Desktop/components/EcomPolicy/hooks';

// constants
import {
  PINCODE_PLACEHOLDER,
  BUTTON_STATE,
  DELIVERY_TEXT,
  DO_NOT_SERVICE_AT_YOUR_LOCATION,
} from './constants';

// analytics
import { onPincodeApply } from './analytics';

function PinCodeServiceNew({
  setIsServiceable,
  isMobile = false,
}: {
  isMobile?: boolean;
  setIsServiceable: (flag: boolean) => void;
}) {
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const [pincode, setPincode] = useState('');
  const [previousPincode, setPreviousPincode] = useState('');
  const [helperText, setHelperText] = useState('');
  const [autofocusState, setAutofocusState] = useState(false);
  const [showApply, setShowApply] = useState(false);

  const [apiResponse, setApiResponse] = useState(getPinCodeDataFromStorage());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (pincode.length < 6) {
      setHelperText('');
      setError(null);
    }
    if (pincode !== '' && pincode === previousPincode) {
      const deliveryText = getPinCodeLabel(apiResponse);
      setHelperText(deliveryText);
      setError(null);
    }
  }, [apiResponse, pincode, previousPincode]);

  useEffect(() => {
    if (isEmpty(apiResponse) === false) {
      const deliveryText = getPinCodeLabel(apiResponse);
      setHelperText(deliveryText);
      setIsServiceable(true);
      setPreviousPincode(apiResponse.pincode);
      setPincode(apiResponse.pincode);
    }
  }, [apiResponse, setIsServiceable]);

  // setting the trailing button value to apply on focus
  useEffect(() => {
    if (inputRef.current === document.activeElement) {
      setShowApply(true);
    }
  }, [setShowApply, inputRef]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length < 6) {
      setIsServiceable(true);
    }
    // Pincode can't have a length greater than 6
    if (e.target.value.length >= 7) {
      return;
    }
    setPincode(e.target.value);
  };

  // Note: this is added for Firefox.
  // Firefox allows users to type in characters for input["number"]
  const handleKeyPress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = new RegExp(/^[0-9]*$/);
    return e.key && !e.key.match(regex) && e.preventDefault();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const regex = new RegExp(/^[0-9]*$/);

    // the value that user is trying to paste
    const pasteData = e.clipboardData.getData('text');

    // if pasteData is of type mumber then only allow pasting the text
    if (pasteData && pasteData.match(regex)) {
      setPincode(pasteData.substring(0, 6));
    }
  };

  const handleClick = async () => {
    try {
      const data = await checkPinCodeService(Number(pincode));
      // analytics event
      onPincodeApply({});

      if (data.response.isServicable === 1) {
        setError(null);
        setApiResponse({ ...data, pincode });
        setPinCodeToStorage(JSON.stringify({ ...data, pincode }));
        const deliveryText = getPinCodeLabel({ ...data, pincode });
        setHelperText(deliveryText);
        setIsServiceable(true);
        return;
      }

      const message = get(data, 'response.msg', '');
      setHelperText(message);

      if (message === DO_NOT_SERVICE_AT_YOUR_LOCATION) {
        setIsServiceable(false);
      }
      setError(data);
    } catch (err) {
      // sentry log
    }
  };

  // on press enter
  const handleFormSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (pincode.length < 6) {
      return;
    }
    // return;
    handleClick();
    setAutofocusState(false);
    inputRef.current && inputRef.current.blur();
    setShowApply(false);
  };

  const onClear = () => {
    setPincode('');
    setIsServiceable(true);
    setAutofocusState(true);
    inputRef.current && inputRef.current.focus();
  };

  const onEdit = () => {
    setAutofocusState(true);
    inputRef.current && inputRef.current.focus();
    if (previousPincode === pincode) return;
    setIsServiceable(true);
    setShowApply(true);
  };

  const onClickOutsidePinCode = (isError) => {
    setShowApply(false);
    if (!isError) {
      setPincode(previousPincode);
      setIsServiceable(true);
    }
  };

  const isError = error !== null;

  useOutsideAlerter(formRef, () => onClickOutsidePinCode(isError));

  const BUTTON_TEXT =
    pincode.length < 6 || (previousPincode !== pincode && !isError) || showApply
      ? BUTTON_STATE.APPLY
      : isError
      ? BUTTON_STATE.CLEAR
      : BUTTON_STATE.EDIT;

  const trailingButtonClick =
    pincode.length < 6 || (previousPincode !== pincode && !isError) || showApply
      ? handleClick
      : isError
      ? onClear
      : onEdit;

  const hideDeliveryText = pincode !== '' && pincode === previousPincode;

  return (
    <PincodeContainer>
      <PincodeInputContainer>
        {!isMobile && !hideDeliveryText && <DeliveryText>{DELIVERY_TEXT}</DeliveryText>}
        {isMobile && <DeliveryText>{DELIVERY_TEXT}</DeliveryText>}
        <form onSubmit={handleFormSubmit} ref={formRef}>
          <PincodeInput
            value={pincode}
            onChange={(e) => handleChange(e)}
            onKeyPress={(e) => handleKeyPress(e)}
            placeholder={PINCODE_PLACEHOLDER}
            type="number"
            isError={isError}
            helperText={helperText}
            label="Enter Pincode"
            autoFocus={autofocusState}
            onFocus={() => {
              setAutofocusState(true);
            }}
            data-cs-mask
            onPaste={onPaste}
            ref={inputRef}
            trailingComponent={
              <TrailingButton
                type="button"
                tabIndex={0}
                onClick={trailingButtonClick}
                aria-label="close"
                disabled={pincode.length !== 6}
              >
                {BUTTON_TEXT}
              </TrailingButton>
            }
          />
        </form>
      </PincodeInputContainer>
    </PincodeContainer>
  );
}

export default memo(PinCodeServiceNew);
